import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateTypingTestModal from './components/CreateTypingTestModal';
import {
  createTypingTest, getTypingTestsByManager, getTypingResults, deleteTypingTest
} from '../../api_service';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending',   color: 'text-warning',  bg: 'bg-warning/10'  },
  COMPLETED: { label: 'Completed', color: 'text-success',  bg: 'bg-success/10'  },
};

const TypingManagement = () => {
  const navigate = useNavigate();
  const managerId = sessionStorage.getItem('userId');

  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [results, setResults] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTests(); }, []);

  const loadTests = async () => {
    setLoading(true);
    try {
      const res = await getTypingTestsByManager(managerId);
      setTests(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setTests([]);
    } finally {
      setLoading(false);
    }
  };

  const loadResults = async (testId) => {
    try {
      const res = await getTypingResults(testId);
      setResults(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setResults([]);
    }
  };

  const handleTestClick = async (item) => {
    setSelectedTest(item);
    await loadResults(item.test.testId);
  };

  const handleCreateTest = async (payload) => {
    await createTypingTest(managerId, payload);
    await loadTests();
  };

  const handleDelete = async (testId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this typing test?')) return;
    await deleteTypingTest(testId);
    if (selectedTest?.test?.testId === testId) {
      setSelectedTest(null);
      setResults([]);
    }
    await loadTests();
  };

  const deadlineLabel = (dateStr) => {
    if (!dateStr) return '';
    const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, cls: 'text-error' };
    if (diff === 0) return { text: 'Due today', cls: 'text-warning' };
    if (diff <= 3) return { text: `${diff}d left`, cls: 'text-warning' };
    return { text: `${diff}d left`, cls: 'text-muted-foreground' };
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userName={sessionStorage.getItem('userName') || 'User'}
        userRole="manager"
        onLogout={() => navigate('/')}
      />
      <main className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Typing Speed Tests</h1>
            <p className="text-sm text-muted-foreground mt-1">Assign typing tests and track trainee speed &amp; accuracy</p>
          </div>
          <Button onClick={() => setShowCreate(true)} iconName="Plus" iconSize={16}>
            Create Test
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Tests', value: tests.length, icon: 'Keyboard', color: 'text-primary' },
            { label: 'Total Assigned', value: tests.reduce((s, t) => s + (t.totalAssigned || 0), 0), icon: 'Users', color: 'text-accent' },
            { label: 'Completed', value: tests.reduce((s, t) => s + (t.completed || 0), 0), icon: 'CheckCircle', color: 'text-success' },
            {
              label: 'Avg WPM',
              value: (() => {
                const withAvg = tests.filter(t => (t.completed || 0) > 0);
                if (withAvg.length === 0) return 0;
                return Math.round(withAvg.reduce((s, t) => s + (t.avgWpm || 0), 0) / withAvg.length);
              })(),
              icon: 'Gauge', color: 'text-warning'
            },
          ].map(card => (
            <div key={card.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name={card.icon} size={18} className={card.color} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{card.value}</p>
                  <p className="text-xs text-muted-foreground">{card.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Test list */}
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Tests ({tests.length})</h2>
            </div>
            {loading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : tests.length === 0 ? (
              <div className="p-8 text-center">
                <Icon name="Keyboard" size={40} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No tests yet. Create your first typing test.</p>
              </div>
            ) : (
              <div className="divide-y divide-border overflow-y-auto max-h-[500px]">
                {tests.map(item => {
                  const dl = deadlineLabel(item.test?.deadline);
                  const isSelected = selectedTest?.test?.testId === item.test?.testId;
                  return (
                    <div
                      key={item.test?.testId}
                      onClick={() => handleTestClick(item)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-2">{item.test?.title}</p>
                        <button
                          onClick={(e) => handleDelete(item.test?.testId, e)}
                          className="text-muted-foreground hover:text-error shrink-0"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={15} />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">{item.test?.durationSeconds}s</span>
                        {dl && <span className={`text-xs ${dl.cls}`}>{dl.text}</span>}
                        <span className="text-xs text-muted-foreground">{item.totalAssigned} trainees</span>
                        {item.completed > 0 && (
                          <span className="text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">{item.completed} done</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Results panel */}
          <div className="lg:col-span-3 bg-card border border-border rounded-xl overflow-hidden">
            {!selectedTest ? (
              <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                <Icon name="MousePointerClick" size={40} className="text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">Select a test to view trainee results</p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-border">
                  <h2 className="font-semibold text-foreground">{selectedTest.test?.title}</h2>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Duration: {selectedTest.test?.durationSeconds}s</span>
                    <span>Deadline: {selectedTest.test?.deadline || 'None'}</span>
                    <span>Avg WPM: {selectedTest.avgWpm || 0}</span>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[460px]">
                  {results.length === 0 ? (
                    <div className="p-8 text-center text-sm text-muted-foreground">No results yet</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground border-b border-border">
                          <th className="px-4 py-2 font-medium">Trainee</th>
                          <th className="px-4 py-2 font-medium">WPM</th>
                          <th className="px-4 py-2 font-medium">Accuracy</th>
                          <th className="px-4 py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {results.map(r => {
                          const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.PENDING;
                          return (
                            <tr key={r.resultId}>
                              <td className="px-4 py-3">
                                <p className="font-medium text-foreground">{r.trainee?.firstname} {r.trainee?.lastname}</p>
                                <p className="text-xs text-muted-foreground">{r.trainee?.emailid}</p>
                              </td>
                              <td className="px-4 py-3 font-semibold text-foreground">
                                {r.status === 'COMPLETED' ? r.wpm : '—'}
                              </td>
                              <td className="px-4 py-3 text-foreground">
                                {r.status === 'COMPLETED' && r.accuracy != null ? `${r.accuracy}%` : '—'}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color}`}>
                                  {cfg.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {showCreate && (
        <CreateTypingTestModal
          managerId={managerId}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreateTest}
        />
      )}
    </div>
  );
};

export default TypingManagement;
